import { useEffect, useState } from 'react'

interface Props {
  iso: string
  tickMs?: number
  noMs?: boolean
  className?: string
  prefix?: string
}

export function RelativeTime({ iso, tickMs, noMs, className, prefix }: Props) {
  const [now, setNow] = useState(() => Date.now())
  const when = isoToMs(iso)

  useEffect(() => {
    if (when == null) return
    const decideTick = () => {
      if (tickMs != null) return tickMs
      const diff = Math.max(0, Date.now() - when)
      if (noMs) return diff < 60_000 ? 1000 : 60_000
      return diff < 60_000 ? 100 : 1000
    }
    let rate = decideTick()
    let id = window.setInterval(tick, rate)
    function tick() {
      setNow(Date.now())
      const next = decideTick()
      if (next !== rate) {
        clearInterval(id)
        rate = next
        id = window.setInterval(tick, rate)
      }
    }
    return () => clearInterval(id)
  }, [when, tickMs, noMs])

  if (when == null) return null
  const label = formatAgo(when, now, !!noMs)
  return (
    <span
      className={className ?? 'text-[10px] text-ink-400'}
      title={new Date(when).toLocaleString()}
    >
      {prefix ?? ''}
      {label}
    </span>
  )
}

function isoToMs(iso: string): number | null {
  const t = Date.parse(iso)
  return Number.isFinite(t) ? t : null
}

export function formatAgo(when: number, now: number, noMs: boolean): string {
  const diff = Math.max(0, now - when)
  if (diff < 1000) return noMs ? 'just now' : `${diff}ms ago`
  if (diff < 60_000) {
    const s = Math.floor(diff / 1000)
    const ms = diff - s * 1000
    return noMs ? `${s}s ago` : `${s}s ${ms.toString().padStart(3, '0')}ms ago`
  }
  if (diff < 3_600_000) {
    const m = Math.floor(diff / 60_000)
    const s = Math.floor((diff - m * 60_000) / 1000)
    return s === 0 ? `${m}m ago` : `${m}m ${s}s ago`
  }
  if (diff < 86_400_000) {
    const h = Math.floor(diff / 3_600_000)
    const m = Math.floor((diff - h * 3_600_000) / 60_000)
    return m === 0 ? `${h}h ago` : `${h}h ${m}m ago`
  }
  if (diff < 30 * 86_400_000) {
    const d = Math.floor(diff / 86_400_000)
    const h = Math.floor((diff - d * 86_400_000) / 3_600_000)
    return h === 0 ? `${d}d ago` : `${d}d ${h}h ago`
  }
  const mo = Math.floor(diff / (30 * 86_400_000))
  return `${mo}mo ago`
}
